import type { ReactNode } from "react";

/**
 * Parses a string with **bold** markdown syntax and returns JSX with <strong> tags.
 *
 * @param text - Text containing **bold** markers
 * @returns Array of ReactNode elements with bold formatting applied
 *
 * @example
 * parseBoldText("**Talk** to Markeroo") // Returns [<strong>Talk</strong>, " to Markeroo"]
 * parseBoldText("Plain text") // Returns ["Plain text"]
 */
export function parseBoldText(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  // Match **text** patterns
  const boldPattern = /\*\*(.+?)\*\*/g;
  let match: RegExpExecArray | null;

  while ((match = boldPattern.exec(text)) !== null) {
    // Add text before the bold section
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index));
    }

    // Add the bold section
    parts.push(<strong key={`bold-${keyCounter++}`}>{match[1]}</strong>);

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text after the last bold section
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  // If no bold sections were found, return the original text
  return parts.length === 0 ? [text] : parts;
}
